import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const initialForm = {
  firstName: '',
  lastName: '',
  email: '',
  company: '',
  service: '',
  budget: '',
  timeline: '',
  message: '',
  source: '',
  privacy: false,
};

const requiredFields = ['firstName', 'lastName', 'email', 'service', 'budget', 'timeline', 'message', 'source'];

const selectOptions = {
  service: [
    'iOS Development',
    'Android Development',
    'Cross-Platform',
    'UI/UX Design',
    'App Strategy',
    'Maintenance & Support',
    'Not Sure Yet',
  ],
  budget: ['Under £10k', '£10k-£25k', '£25k-£50k', '£50k-£100k', '£100k+', 'Not Sure Yet'],
  timeline: ['ASAP', '1-3 Months', '3-6 Months', '6+ Months', 'Flexible'],
  source: ['Google Search', 'LinkedIn', 'Referral', 'Social Media', 'Other'],
};

const faqs = [
  [
    'How quickly will you respond to my enquiry?',
    "We'll reply to every qualified enquiry within 24 hours, usually much sooner during UK working hours.",
  ],
  [
    'Do you sign NDAs before discussing project details?',
    'Yes. We can sign an NDA before reviewing sensitive ideas, documentation, or product plans.',
  ],
  [
    'Can I get a quote before committing?',
    'Yes. After a discovery call, we can provide a tailored estimate with scope, timeline, and engagement options.',
  ],
  [
    'Do you work with clients outside the UK?',
    'Yes. Milestor supports clients globally and works across timezones with clear async communication.',
  ],
  [
    "What's the best way to share my project brief?",
    'Use the form or bring notes to a discovery call. Rough ideas are welcome.',
  ],
];

const trustSignals = [
  ['🔒', 'NDA Protected', 'Your ideas are always safe with us.'],
  ['⚡', '24hr Response', 'We get back to every enquiry within a day.'],
  ['🌍', 'Global Clients', "We've worked with clients across 20+ countries."],
  ['💬', 'No Obligation', 'Free discovery calls, zero pressure.'],
];

function ContactPage() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [openFaq, setOpenFaq] = useState(0);
  const fieldRefs = useRef({});

  const setField = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: '' }));
    if (status === 'success') {
      setStatus('idle');
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    requiredFields.forEach((field) => {
      if (!form[field].trim()) {
        nextErrors[field] = 'This field is required.';
      }
    });

    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) {
      nextErrors.email = 'Please enter a valid email address.';
    }

    if (!form.privacy) {
      nextErrors.privacy = 'Please confirm your consent before sending.';
    }

    setErrors(nextErrors);
    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validateForm();
    const firstError = Object.keys(nextErrors)[0];

    if (firstError) {
      fieldRefs.current[firstError]?.focus();
      return;
    }

    setStatus('loading');
    window.setTimeout(() => {
      setStatus('success');
      setForm(initialForm);
    }, 900);
  };

  const renderInput = (name, label, type = 'text', optional = false) => (
    <div className={`form-field ${errors[name] ? 'form-field--error' : ''}`}>
      <label htmlFor={name}>
        {label} {optional && <span>(optional)</span>}
      </label>
      <input
        id={name}
        ref={(element) => {
          fieldRefs.current[name] = element;
        }}
        type={type}
        value={form[name]}
        aria-invalid={Boolean(errors[name])}
        aria-describedby={errors[name] ? `${name}-error` : undefined}
        onChange={(event) => setField(name, event.target.value)}
      />
      {errors[name] && <small id={`${name}-error`}>{errors[name]}</small>}
    </div>
  );

  const renderSelect = (name, label, placeholder) => (
    <div className={`form-field ${errors[name] ? 'form-field--error' : ''}`}>
      <label htmlFor={name}>{label}</label>
      <select
        id={name}
        ref={(element) => {
          fieldRefs.current[name] = element;
        }}
        value={form[name]}
        aria-invalid={Boolean(errors[name])}
        aria-describedby={errors[name] ? `${name}-error` : undefined}
        onChange={(event) => setField(name, event.target.value)}
      >
        <option value="">{placeholder}</option>
        {selectOptions[name].map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      {errors[name] && <small id={`${name}-error`}>{errors[name]}</small>}
    </div>
  );

  return (
    <main className="contact-page">
      <section className="contact-hero" aria-labelledby="contact-hero-title">
        <div className="contact-hero__inner">
          <p className="contact-breadcrumb">
            <Link to="/">Home</Link> / Contact
          </p>
          <p className="hero-chip">✦ Let's Build Together</p>
          <h1 id="contact-hero-title" className="contact-hero__title">
            <span>Start Your Project.</span>
            <span>We'd Love to Hear</span>
            <span>From You.</span>
          </h1>
          <p>Tell us about your idea and we'll get back to you within 24 hours.</p>
        </div>
      </section>

      <section className="contact-main" id="contact-form" aria-labelledby="contact-form-title">
        <div className="contact-main__inner">
          <div className="contact-form-card reveal">
            <span className="contact-label">SEND US A MESSAGE</span>
            <h2 id="contact-form-title">Tell Us About Your Project</h2>
            <form noValidate onSubmit={handleSubmit}>
              <div className="form-row">
                {renderInput('firstName', 'First Name')}
                {renderInput('lastName', 'Last Name')}
              </div>
              {renderInput('email', 'Email Address', 'email')}
              {renderInput('company', 'Company / Organisation', 'text', true)}
              {renderSelect('service', 'Service Interested In', 'Select a service')}
              {renderSelect('budget', 'Project Budget', 'Select a budget')}
              {renderSelect('timeline', 'Project Timeline', 'Select a timeline')}
              <div className={`form-field ${errors.message ? 'form-field--error' : ''}`}>
                <label htmlFor="message">Tell us about your project</label>
                <textarea
                  id="message"
                  ref={(element) => {
                    fieldRefs.current.message = element;
                  }}
                  rows="5"
                  value={form.message}
                  placeholder="Describe your idea, goals, and any specific requirements..."
                  aria-invalid={Boolean(errors.message)}
                  aria-describedby={errors.message ? 'message-error' : undefined}
                  onChange={(event) => setField('message', event.target.value)}
                />
                {errors.message && <small id="message-error">{errors.message}</small>}
              </div>
              {renderSelect('source', 'How did you hear about us?', 'Select an option')}
              <label className={`privacy-check ${errors.privacy ? 'privacy-check--error' : ''}`}>
                <input
                  ref={(element) => {
                    fieldRefs.current.privacy = element;
                  }}
                  type="checkbox"
                  checked={form.privacy}
                  onChange={(event) => setField('privacy', event.target.checked)}
                />
                <span aria-hidden="true" />
                <b>
                  I agree to Milestor's Privacy Policy and consent to being contacted about my project.
                </b>
              </label>
              {errors.privacy && <small className="privacy-error">{errors.privacy}</small>}
              <button className={`contact-submit contact-submit--${status}`} type="submit" disabled={status === 'loading'}>
                {status === 'loading' && <span className="submit-spinner" aria-hidden="true" />}
                {status === 'success' ? "✓ Message sent! We'll be in touch within 24 hours." : status === 'loading' ? 'Sending...' : 'Send Message →'}
              </button>
            </form>
          </div>

          <aside className="contact-sidebar" aria-label="Contact information">
            <section className="contact-info-card reveal">
              <h2>Contact Details</h2>
              <ul>
                <li><span>🕐</span><b>Response time</b><p>Within 24 hours</p></li>
              </ul>
            </section>
            <section className="discovery-card reveal">
              <h2>Book a Free Discovery Call</h2>
              <p>Prefer to talk? Schedule a 30-minute free call with our team.</p>
              <a className="button button--primary" href="#contact-form">
                Book a Call →
              </a>
              <small>No obligation. No sales pressure.</small>
            </section>
          </aside>
        </div>
      </section>

      <section className="contact-faq" aria-labelledby="contact-faq-title">
        <div className="contact-section-heading reveal">
          <span className="contact-label">QUICK ANSWERS</span>
          <h2 id="contact-faq-title">Before You Reach Out</h2>
          <p>A few answers to common pre-contact questions.</p>
        </div>
        <div className="faq-list reveal">
          {faqs.map(([question, answer], index) => {
            const isOpen = openFaq === index;

            return (
              <article className={`faq-item ${isOpen ? 'faq-item--open' : ''}`} key={question}>
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={`contact-faq-answer-${index}`}
                  onClick={() => setOpenFaq(isOpen ? -1 : index)}
                >
                  <span>{question}</span>
                  <b aria-hidden="true">{isOpen ? '×' : '+'}</b>
                </button>
                <div id={`contact-faq-answer-${index}`} className="faq-item__answer">
                  <p>{answer}</p>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="trust-strip" aria-label="Why contact Milestor">
        {trustSignals.map(([icon, title, text]) => (
          <article className="trust-strip__item reveal" key={title}>
            <span>{icon}</span>
            <div>
              <h3>{title}</h3>
              <p>{text}</p>
            </div>
          </article>
        ))}
      </section>

      <section className="cta-banner" aria-labelledby="contact-cta-title">
        <div className="cta-banner__inner reveal">
          <h2 id="contact-cta-title">Your Next App Starts with a Conversation.</h2>
          <p>Don't overthink it — just say hello.</p>
          <div className="cta-banner__actions">
            <a className="button button--light" href="#contact-form">
              Send Us a Message ↑
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}

export default ContactPage;
